import type { JSX } from 'react';

import classNames from 'classnames';
import { Search } from 'lucide-react';

import { useCommandPalette } from './CommandPaletteContext';

import styles from './CommandTrigger.module.css';

interface CommandTriggerProps {
  /** 'sidebar' (default, dark glass, full-width) or 'topbar' (compact, theme-aware). */
  variant?: 'sidebar' | 'topbar';
}

/**
 * Search trigger that opens the global command palette. Cmd/Ctrl+K also opens
 * it via the provider's document-level listener. Rendered in the page top nav
 * ('topbar' variant); the 'sidebar' variant is kept for backward compatibility.
 */
export function CommandTrigger({ variant = 'sidebar' }: CommandTriggerProps): JSX.Element {
  const { toggle } = useCommandPalette();

  const shortcutLabel =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform)
      ? '⌘K'
      : 'Ctrl+K';

  return (
    <button
      type="button"
      className={classNames(styles.Trigger, { [styles.TopBar]: variant === 'topbar' })}
      onClick={toggle}
      aria-label={`Open command palette (${shortcutLabel})`}
    >
      <Search className={styles.Icon} aria-hidden="true" />
      <span className={styles.Label}>{variant === 'topbar' ? 'Search…' : 'Jump to…'}</span>
      <kbd className={styles.Kbd}>{shortcutLabel}</kbd>
    </button>
  );
}

export default CommandTrigger;
